import type { ReactNode } from 'react';
import { useState } from 'react';
import type { SizeChangeCallback } from '../utils';
import { useElementSize } from '../utils';
import {
  ArrowsContainer,
  LayoutContainer,
  PaginationContainer
} from './Containers';

const ARROWS_CONTAINER_WIDTH = 112;

export interface LayoutProps {
  prevArrow: ReactNode;
  nextArrow: ReactNode;
  pagination: ReactNode;
  position: string;
  onSizeChange: SizeChangeCallback;
  isMobile?: boolean;
}

export function BottomCenterLayout({
  prevArrow,
  nextArrow,
  pagination,
  position,
  onSizeChange
}: LayoutProps) {
  const [
    containerRef,
    { width: containerWidth, scrollWidth: containerScrollWidth }
  ] = useElementSize(onSizeChange);

  if (pagination === null) {
    return (
      <LayoutContainer
        position={position}
        ref={containerRef}
        containerWidth={containerWidth}
        containerScrollWidth={containerScrollWidth}
      >
        <ArrowsContainer position={position}>
          {prevArrow}
          {nextArrow}
        </ArrowsContainer>
      </LayoutContainer>
    );
  }

  return (
    <LayoutContainer
      position={position}
      ref={containerRef}
      containerWidth={containerWidth}
      containerScrollWidth={containerScrollWidth}
    >
      {prevArrow}
      {pagination}
      {nextArrow}
    </LayoutContainer>
  );
}

export function BottomLeftLayout({
  prevArrow,
  nextArrow,
  pagination,
  position,
  isMobile,
  onSizeChange
}: LayoutProps) {
  const [
    containerRef,
    { width: containerWidth, scrollWidth: containerScrollWidth }
  ] = useElementSize(onSizeChange);

  const [paginationScrollWidth, setPaginationScrollWidth] = useState(0);

  const onPaginationSizeChange = ({ scrollWidth }: { scrollWidth: number }) => {
    setPaginationScrollWidth(scrollWidth);
  };

  const isArrowsContainerNarrow =
    containerWidth - paginationScrollWidth < ARROWS_CONTAINER_WIDTH;
  const withoutArrows = !prevArrow && !nextArrow;
  const shouldCenterPagination = !isMobile || withoutArrows;

  return (
    <LayoutContainer
      position={position}
      ref={containerRef}
      containerWidth={containerWidth}
      containerScrollWidth={containerScrollWidth}
    >
      <ArrowsContainer position={position} isNarrow={isArrowsContainerNarrow}>
        {prevArrow}
        {nextArrow}
      </ArrowsContainer>
      <PaginationContainer
        position={position}
        shouldCenter={shouldCenterPagination}
        onSizeChange={onPaginationSizeChange}
      >
        {pagination}
      </PaginationContainer>
    </LayoutContainer>
  );
}

export function BottomRightLayout({
  prevArrow,
  nextArrow,
  pagination,
  position,
  isMobile,
  onSizeChange
}: LayoutProps) {
  const [
    containerRef,
    { width: containerWidth, scrollWidth: containerScrollWidth }
  ] = useElementSize(onSizeChange);

  const [paginationScrollWidth, setPaginationScrollWidth] = useState(0);

  const onPaginationSizeChange = ({ scrollWidth }: { scrollWidth: number }) => {
    setPaginationScrollWidth(scrollWidth);
  };

  const isArrowsContainerNarrow =
    containerWidth - paginationScrollWidth < ARROWS_CONTAINER_WIDTH;

  const withoutArrows = !prevArrow && !nextArrow;
  const shouldCenterPagination = !isMobile || withoutArrows;

  return (
    <LayoutContainer
      position={position}
      ref={containerRef}
      containerWidth={containerWidth}
      containerScrollWidth={containerScrollWidth}
    >
      <PaginationContainer
        position={position}
        shouldCenter={shouldCenterPagination}
        onSizeChange={onPaginationSizeChange}
      >
        {pagination}
      </PaginationContainer>
      <ArrowsContainer position={position} isNarrow={isArrowsContainerNarrow}>
        {prevArrow}
        {nextArrow}
      </ArrowsContainer>
    </LayoutContainer>
  );
}

export function OtherLayout({
  prevArrow,
  nextArrow,
  pagination,
  position
}: LayoutProps) {
  return (
    <LayoutContainer position={position}>
      <ArrowsContainer position={position}>
        {prevArrow}
        {nextArrow}
      </ArrowsContainer>
      {pagination}
    </LayoutContainer>
  );
}
